import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  Pagination,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  Tabs,
  Grid,
  TextField,
  InputAdornment,
  Tab,
  useTheme,
  Chip
} from '@mui/material';
import {
  Visibility,
  Delete,
  WarningAmber,
  Search,
  Clear,
  PersonAdd,
  Edit
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import palette from 'src/theme/palette';
import Page from 'src/components/Page';
import { get_valuation_tool, delete_valuation_tool, addEditleads } from 'src/ApiServices';
import VToolViewModal from 'src/sections/ValuationTools/VToolViewModal';
import VToolAddModal from 'src/sections/ValuationTools/VToolAddModal';
import VToolDeleteModal from 'src/sections/ValuationTools/VToolDeleteModal';
import VToolMoveLeadsModal from 'src/sections/ValuationTools/VToolMoveLeadsModal';
import VToolReplyModal from 'src/sections/ValuationTools/VToolReplyModal';
import AddIcon from '@mui/icons-material/Add';
import { Reply } from '@mui/icons-material';

export default function ValuationTool() {
  // State Management
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [valuationData, setValuationData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [selectedValuation, setSelectedValuation] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [addEditModalOpen, setAddEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [moveToLeadsModalOpen, setMoveToLeadsModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [moveToLeadsLoading, setMoveToLeadsLoading] = useState(false);
  const [replyModalOpen, setReplyModalOpen] = useState(false);
  const [replyLoading, setReplyLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 20,
    total: 0,
    last_page: 1,
    has_more_pages: false
  });

  const transformValuationData = (apiData) => {
    return apiData.map((valuation) => {
      let cityName = '';
      if (valuation.city) {
        if (typeof valuation.city === 'object' && valuation.city.name) {
          cityName = valuation.city.name;
        } else if (valuation.city_id && valuation.city.name) {
          cityName = valuation.city.name;
        }
      }

      let stateName = '-';
      if (valuation.state) {
        if (typeof valuation.state === 'object' && valuation.state.name) {
          stateName = valuation.state.name;
        } else if (valuation.state_id && valuation.state.name) {
          stateName = valuation.state.name;
        }
      }

      let propertyTypeName = '-';
      if (valuation.property_type && valuation.property_type.name) {
        propertyTypeName = valuation.property_type.name;
      }

      let areaUnitTypeName = '-';
      if (valuation.area_unit_type && valuation.area_unit_type.name) {
        areaUnitTypeName = valuation.area_unit_type.name;
      }

      return {
        id: valuation.id || '-',
        name: valuation.name || '-',
        email: valuation.email || '-',
        phone: valuation.phone || '-',
        address: valuation.address || '-',
        property_type_id: valuation.property_type_id || '-',
        property_type_name: propertyTypeName,
        bed_rooms: valuation.bed_rooms || '-',
        bath_rooms: valuation.bath_rooms || '-',
        square_feet: valuation.square_feet || '-',
        state_id: valuation.state_id || '-',
        state_name: stateName,
        city_id: valuation.city_id || '-',
        city_name: cityName,
        build_year: valuation.build_year || '-',
        area_size: valuation.area_size || '-',
        area_unit_type_id: valuation.area_unit_type_id || '-',
        area_unit_type_name: areaUnitTypeName,
        condition: valuation.condition || '-',
        status: valuation.status || '2',
        created_at: valuation.created_at || '-',
        updated_at: valuation.updated_at || '-',
        original_city: valuation.city,
        original_state: valuation.state,
        original_property_type: valuation.property_type,
        original_area_unit_type: valuation.area_unit_type
      };
    });
  };

  // API Calls
  const fetchValuationData = async (currentPage = 1, search = '') => {
    try {
      setLoading(true);
      const response = await get_valuation_tool({
        page: currentPage,
        search: search
      });
      const transformedData = transformValuationData(response.data || []);
      setValuationData(transformedData);
      setPagination(
        response.pagination || {
          current_page: 1,
          per_page: 20,
          total: response.data?.length || 0,
          last_page: 1,
          has_more_pages: false
        }
      );
    } catch (error) {
      console.error('Error fetching valuation tools:', error);
      setValuationData([]);
      toast.error('Failed to load valuation tools');
    } finally {
      setLoading(false);
    }
  };

  // Effects
  useEffect(() => {
    if (activeTab === 0) {
      fetchValuationData(page, searchQuery);
    }
  }, [activeTab, page]);

  // Event Handlers
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setPage(1);
    setSearchQuery('');
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };
  // Search handlers
  const handleSearchSubmit = useCallback(async () => {
    setPage(1);
    if (activeTab === 0) {
      await fetchValuationData(1, searchQuery);
    }
  }, [searchQuery, activeTab]);

  const handleClearSearch = useCallback(async () => {
    setSearchQuery('');
    setPage(1);
    if (activeTab === 0) {
      await fetchValuationData(1, '');
    }
  }, [activeTab]);

  const handleReplyClick = (valuation) => {
    setSelectedValuation(valuation);
    setReplyModalOpen(true);
  };

  const handleReplyClose = () => {
    setReplyModalOpen(false);
    setSelectedValuation(null);
  };

  const handleReplySubmit = async (formData) => {
    try {
      setReplyLoading(true);
      // API CALL HERE
      toast.success('Reply sent successfully!');
      handleReplyClose();
    } catch (error) {
      console.error('Failed to send reply:', error);
      toast.error(error.response?.data?.message || 'Failed to send reply');
    } finally {
      setReplyLoading(false);
    }
  };
  const handleViewClick = (valuation) => {
    setSelectedValuation(valuation);
    setViewModalOpen(true);
  };

  const handleEditClick = (valuation) => {
    setSelectedValuation(valuation);
    setAddEditModalOpen(true);
  };

  const handleAddClick = () => {
    setSelectedValuation(null);
    setAddEditModalOpen(true);
  };

  const handleViewClose = () => {
    setViewModalOpen(false);
    setSelectedValuation(null);
  };

  const handleAddEditClose = () => {
    setAddEditModalOpen(false);
    setSelectedValuation(null);
  };

  // Delete Operations
  const handleDeleteClick = (valuation) => {
    setSelectedValuation(valuation);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedValuation) return;

    try {
      setDeleteLoading(true);
      await delete_valuation_tool(selectedValuation.id);
      toast.success('Valuation tool deleted successfully!');
      fetchValuationData(page, searchQuery);
      setDeleteModalOpen(false);
      setSelectedValuation(null);
    } catch (error) {
      console.error('Failed to delete valuation tool:', error);
      if (error.response?.status === 404) {
        toast.error('Valuation tool not found!');
      } else if (error.response?.status === 403) {
        toast.error('Permission denied!');
      } else if (error.response?.status === 401) {
        toast.error('Unauthorized! Please login again.');
      } else {
        toast.error(error.response?.data?.message || 'Failed to delete valuation tool!');
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setSelectedValuation(null);
  };

  // Move to Leads Operations
  const handleMoveToLeadsClick = (valuation) => {
    setSelectedValuation(valuation);
    setMoveToLeadsModalOpen(true);
  };

  const handleMoveToLeadsConfirm = async () => {
    if (!selectedValuation) return;

    try {
      setMoveToLeadsLoading(true);

      const leadsPayload = {
        contact: selectedValuation.phone,
        email: selectedValuation.email,
        lead_type: 3,
        name: selectedValuation.name,
        note: `Valuation Tool Entry - Address: ${selectedValuation.address}`,
        property_id: '',
        lead_type_id: 4,
        valuation_tool_id: selectedValuation.id
      };

      await addEditleads(leadsPayload);
      toast.success('Valuation successfully moved to leads!');
      fetchValuationData(page, searchQuery);
      setMoveToLeadsModalOpen(false);
      setSelectedValuation(null);
    } catch (error) {
      console.error('Failed to move valuation to leads:', error);
      if (error.response && error.response.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to move to leads');
      }
    } finally {
      setMoveToLeadsLoading(false);
    }
  };

  const handleMoveToLeadsCancel = () => {
    setMoveToLeadsModalOpen(false);
    setSelectedValuation(null);
  };

  // Utility Functions
  const truncateText = (text, maxLength = 30) => {
    if (!text) return 'N/A';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  const getConditionColor = (condition) => {
    switch (condition) {
      case 'good':
        return 'success';
      case 'fair':
        return 'warning';
      case 'poor':
        return 'error';
      default:
        return 'default';
    }
  };

  // Table Configuration
  const valuationTableHeaders = [
    // 'ID',
    'Name',
    'Email',
    'Phone',
    'Address',
    'Condition',
    'Date',
    'Action'
  ];

  // Render Functions
  const renderSkeletonRows = (headers) =>
    Array.from({ length: 5 }).map((_, index) => (
      <TableRow key={index}>
        {headers.map((_, cellIndex) => (
          <TableCell key={cellIndex} sx={{ px: 2 }}>
            <Skeleton variant="text" height={25} />
          </TableCell>
        ))}
      </TableRow>
    ));

  const renderNoRecordsFound = (headers, message) => (
    <TableRow>
      <TableCell colSpan={headers.length} sx={{ textAlign: 'center', py: 8, borderBottom: 'none' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography
            variant="h6"
            sx={{ color: palette.text.secondary, fontFamily: 'poppinsMedium', mb: 1 }}
          >
            {message.title}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: palette.grey[600],
              fontFamily: 'poppinsRegular',
              maxWidth: 400,
              textAlign: 'center'
            }}
          >
            {message.description}
          </Typography>
        </Box>
      </TableCell>
    </TableRow>
  );

  // Valuation Table Component
  const ValuationTable = () => (
    <Card>
      <TableContainer>
        <Table
          sx={{
            '& .MuiTableCell-head': {
              backgroundColor: palette.background.paper,
              color: palette.primary.tableHeadTitle,
              fontFamily: 'poppinsSemiBold',
              fontSize: '14px',
              borderBottom: `2px solid ${palette.divider}`,
              py: 2
            },
            '& .MuiTableCell-body': {
              color: palette.text.primary,
              fontFamily: 'poppinsRegular',
              fontSize: '14px',
              borderBottom: `1px solid ${palette.divider}`,
              py: 2
            }
          }}
        >
          <TableHead>
            <TableRow>
              {valuationTableHeaders.map((header, index) => (
                <TableCell key={index} sx={{ px: 2, whiteSpace: 'nowrap' }}>
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading
              ? renderSkeletonRows(valuationTableHeaders)
              : valuationData.length > 0
              ? valuationData.map((valuation) => (
                  <TableRow
                    key={valuation.id}
                    sx={{
                      '&:hover': {
                        backgroundColor: palette.action.hover
                      }
                    }}
                  >
                    <TableCell sx={{ px: 2 }}>{valuation.name}</TableCell>
                    <TableCell sx={{ px: 2 }}>{valuation.email}</TableCell>
                    <TableCell sx={{ px: 2 }}>{valuation.phone}</TableCell>
                    <TableCell sx={{ px: 2 }}>
                      <Tooltip title={valuation.address || '-'} arrow>
                        <Typography
                          component="span"
                          sx={{
                            display: 'inline-block',
                            maxWidth: 200,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            cursor: 'pointer'
                          }}
                        >
                          {truncateText(valuation.address, 25)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ px: 2 }}>
                      <Chip
                        label={valuation.condition?.toUpperCase() || 'N/A'}
                        color={getConditionColor(valuation.condition)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>

                    <TableCell sx={{ px: 2 }}>
                      {new Date(valuation.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell sx={{ px: 2 }}>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Reply">
                          <IconButton
                            size="small"
                            onClick={() => handleReplyClick(valuation)}
                            sx={{
                              color: palette.info.main,
                              '&:hover': {
                                backgroundColor: palette.info.lighter
                              }
                            }}
                          >
                            {/* <Settings />  */}
                            <Reply />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleViewClick(valuation)}
                            sx={{
                              color: palette.primary.main,
                              '&:hover': {
                                backgroundColor: palette.primary.lighter
                              }
                            }}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                        {/* <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleEditClick(valuation)}
                            sx={{
                              color: palette.info.main,
                              '&:hover': {
                                backgroundColor: palette.info.lighter
                              }
                            }}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip> */}
                        <Tooltip title="Move to Leads">
                          <IconButton
                            size="small"
                            onClick={() => handleMoveToLeadsClick(valuation)}
                            sx={{
                              color: palette.success.main,
                              '&:hover': {
                                backgroundColor: palette.success.lighter
                              }
                            }}
                          >
                            <PersonAdd />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(valuation)}
                            sx={{
                              color: palette.error.main,
                              '&:hover': {
                                backgroundColor: palette.error.lighter
                              }
                            }}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              : renderNoRecordsFound(valuationTableHeaders, {
                  title: 'No Valuation Tools Found',
                  description: searchQuery
                    ? `No valuation tools found for "${searchQuery}". Try a different search term.`
                    : 'No valuation tools available at the moment.'
                })}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );

  return (
    <Page title={`Valuation Tools Inquiries | ${process.env.REACT_APP_PROJECTNAME}`}>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
            mt: 2,
            pb: 2,
            flexWrap: 'wrap',
            gap: 2
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: 20, md: 25 },
              fontFamily: 'poppinsSemiBold',
              color: palette.primary.titledark
            }}
          >
            Valuation Tools Inquiries
          </Typography>
          {/* <Button
            variant="contained"
            onClick={handleAddClick}
            sx={{
              backgroundColor: palette.primary.main,
              color: palette.primary.contrastText,
              fontFamily: 'poppinsMedium',
              '&:hover': {
                backgroundColor: palette.primary.dark
              }
            }}
          >
              <AddIcon />
            Add New Valuation
          </Button> */}
        </Box>

        {/* Search Section */}
        <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <Grid item xs={12} md={10}>
            <TextField
              variant="outlined"
              placeholder="Search valuation tools..."
              size="medium"
              fullWidth
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearchSubmit();
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: palette.text.secondary }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClearSearch} edge="end">
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{
                backgroundColor: theme.palette.background.paper,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: theme.palette.divider },
                  '&:hover fieldset': { borderColor: theme.palette.primary.main },
                  '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main }
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleSearchSubmit}
                fullWidth
                sx={{
                  height: 56,
                  fontSize: '14px',
                  fontFamily: 'poppinsMedium'
                }}
                startIcon={<Search />}
              >
                Search
              </Button>
            </Box>
          </Grid>
        </Grid>

        {/* Valuation Table */}
        <ValuationTable />

        {/* Pagination for Valuation Tools */}
        {!loading && valuationData.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'end', mt: 3 }}>
            <Pagination
              count={pagination.last_page}
              page={pagination.current_page}
              onChange={handlePageChange}
              color="primary"
            />
          </Box>
        )}

        {/* Modals */}
        {/* <VToolViewModal
          open={viewModalOpen}
          onClose={handleViewClose}
          valuation={selectedValuation}
        /> */}

        <VToolViewModal
          open={viewModalOpen}
          onClose={handleViewClose}
          valuation={selectedValuation}
          onReplyClick={(valuation) => {
            handleViewClose();
            handleReplyClick(valuation);
          }}
        />

        {/* <VToolAddModal
          open={addEditModalOpen}
          onClose={handleAddEditClose}
          valuation={selectedValuation}
          refreshData={() => fetchValuationData(page, searchQuery)}
        /> */}

        <VToolReplyModal
          open={replyModalOpen}
          onClose={handleReplyClose}
          valuation={selectedValuation}
          onSubmit={handleReplySubmit}
          loading={replyLoading}
        />

        <VToolDeleteModal
          open={deleteModalOpen}
          onClose={handleDeleteCancel}
          valuation={selectedValuation}
          onConfirm={handleDeleteConfirm}
          loading={deleteLoading}
        />

        <VToolMoveLeadsModal
          open={moveToLeadsModalOpen}
          onClose={handleMoveToLeadsCancel}
          valuation={selectedValuation}
          onConfirm={handleMoveToLeadsConfirm}
          loading={moveToLeadsLoading}
        />
      </Box>
    </Page>
  );
}
